import { useCallback, useEffect, useRef, useState } from "react";
import { openSecondScreenStream } from "../../../utils/secondScreenStream";

export type SecondScreenStatus = "idle" | "pairing" | "paired";

/**
 * Shown whenever this desktop stops being paired because the phone is the
 * one that left, whether that arrived as a live `revoked` SSE event or was
 * only discovered after the fact by revalidateAfterReconnect below. Same
 * fact either way, so it gets the same explanation rather than the
 * revalidation path leaving the fan to wonder why their panel came back.
 */
const PHONE_GONE_MESSAGE = "Your phone disconnected, so this screen has taken over again.";

export interface SecondScreenCode {
  sessionId: string;
  pin: string;
  qrSvg: string;
  expiresAt: string;
}

export interface SecondScreenSession {
  status: SecondScreenStatus;
  /** Set while status is "pairing". */
  code: SecondScreenCode | null;
  error: string | null;
  /**
   * Whether the session stream (the desktop's liveness signal to the
   * server) is actually up right now. False while a reconnect is in
   * flight: the stream can drop and come back on its own (a wifi blip, the
   * laptop sleeping, an ECS task cycling on deploy) without the pairing
   * itself ever having ended, and a fan watching this go false and back to
   * true is the only way to tell "still working, just reconnecting" apart
   * from a silent failure that is quietly running out the server's grace
   * period.
   */
  connected: boolean;
  /**
   * The session this end is watching, or null when there is none.
   *
   * Held in a ref internally, because disconnect() needs it without
   * re-rendering; exposed as state because the command executor needs it as a
   * dependency, and a ref would leave it holding whatever value existed when
   * it was first read.
   */
  sessionId: string | null;
  requestPairing(): Promise<void>;
  cancelPairing(): void;
  disconnect(): Promise<void>;
}

interface Params {
  gameId: string;
  jwt: string | null;
  apiBaseUrl?: string;
  /**
   * Whether this tenant even has the Audience Layer switched on. Defaults to
   * true so a caller that never passes it keeps behaving exactly as before.
   * The Watch page passes its own `audienceLayerEnabled`: without this gate,
   * a tenant with the layer off still fires `GET /second-screen/active` on
   * every Watch page load, and with `VITE_AUDIENCE_LAYER_API_URL` unset that
   * request lands on the SPA's own origin and tries to parse an HTML
   * document as JSON.
   */
  enabled?: boolean;
  /**
   * A command the paired phone has issued, by id.
   *
   * This hook owns the session stream, so it is the only place these can
   * arrive, but running one is somebody else's job (see
   * useSecondScreenCommands). Reporting it and leaving it there keeps this
   * hook about the pairing and nothing else.
   *
   * Optional, so a caller that has not wired up the executor simply ignores
   * commands rather than failing on them.
   */
  onCommand?: (commandId: string) => void;
  /**
   * Renews this page's session, for the stream alone.
   *
   * The open stream is what tells the server this desktop is still here, and
   * it carries the tenant access token. That token is good for minutes while a
   * pairing runs for hours, so without this an ordinary expiry came back 401,
   * which the stream treats as terminal, and a live pairing ended with "reload
   * the page to pair a phone again".
   */
  refreshAuth?: () => Promise<string | null>;
}

/**
 * Holds the pairing session for the Audience Layer's "hand this to my
 * phone" flow on the Watch page: whether a phone already owns this game,
 * the code to show while one is pairing, and the stream that tells this
 * hook the moment either of those changes.
 */
export function useSecondScreenSession({
  gameId,
  jwt,
  apiBaseUrl,
  enabled = true,
  onCommand,
  refreshAuth,
}: Params): SecondScreenSession {
  const [status, setStatus] = useState<SecondScreenStatus>("idle");
  const [code, setCode] = useState<SecondScreenCode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);

  // Read inside callbacks without making jwt/apiBaseUrl part of every
  // callback's dependency array; kept current on every render.
  const jwtRef = useRef(jwt);
  jwtRef.current = jwt;
  const baseRef = useRef(apiBaseUrl ?? "");
  baseRef.current = apiBaseUrl ?? "";
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  /*
   * A ref rather than a dependency, deliberately.
   *
   * The stream's onEvent closure is created once per connection, and the
   * caller wires this up with an inline arrow function. Listing it as a
   * dependency would tear down and reopen the stream on every render of the
   * Watch page, and the open stream is precisely what tells the server this
   * desktop is still here.
   */
  const onCommandRef = useRef(onCommand);
  onCommandRef.current = onCommand;
  // Read through a ref for the same reason: the stream holds this for the life
  // of a connection and must not be reopened when the caller re-renders.
  const refreshAuthRef = useRef(refreshAuth);
  refreshAuthRef.current = refreshAuth;

  // The session this end is currently watching, independent of `code`: once
  // paired, `code` is cleared, but disconnect() still needs the id.
  const sessionIdRef = useRef<string | null>(null);
  /*
   * The same value, mirrored into state, because it is now read by something
   * outside this hook.
   *
   * The ref is what disconnect() and the stream callbacks read, and it must
   * stay a ref: they run long after the render that set it and must not
   * re-subscribe when it changes. But the command executor takes the id as a
   * dependency, and a ref hands it whatever value existed when it was first
   * read. Both, therefore, written together through setSessionId below.
   */
  const [sessionId, setSessionIdState] = useState<string | null>(null);
  const setSessionId = useCallback((id: string | null) => {
    sessionIdRef.current = id;
    setSessionIdState(id);
  }, []);
  // Closes the currently open stream, including its own reconnect loop.
  // Returned by openSecondScreenStream; null when nothing is open.
  const streamDisposeRef = useRef<(() => void) | null>(null);
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards state updates that arrive after unmount, from a stream event
  // landing in the gap between the effect's cleanup and the fetch loop
  // noticing its signal was aborted.
  const mountedRef = useRef(true);

  const clearExpiryTimer = useCallback(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  }, []);

  /**
   * Closes whatever stream is open. This is the mechanism, not housekeeping:
   * the open stream is what tells the server this end is still on the game
   * page, so leaving one open after unmount, or after a new one takes its
   * place, would keep a phone paired to a page the fan has left.
   */
  const closeStream = useCallback(() => {
    streamDisposeRef.current?.();
    streamDisposeRef.current = null;
  }, []);

  /**
   * Whatever ends this pairing while it is live: the phone left (a
   * `revoked` SSE event) or the stream itself came back permanently dead
   * (401/403/404: see openSecondScreenStream's onTerminal). Same outcome
   * either way, so both paths land here instead of duplicating it.
   */
  const endSession = useCallback(
    (message: string | null) => {
      closeStream();
      clearExpiryTimer();
      setSessionId(null);
      setStatus("idle");
      setCode(null);
      setConnected(false);
      setError(message);
    },
    [clearExpiryTimer, closeStream],
  );

  /**
   * Belt and braces after a reconnect: the stream coming back up does not by
   * itself prove the pairing is still real, only that this desktop can talk
   * to the server again. A phone could have disconnected (or a new pairing
   * replaced this one) while this end's own stream was down and unable to
   * hear the `revoked` event for it, which would otherwise leave "Connected
   * to Your Phone" on screen forever. `GET /second-screen/active` is the
   * same check the initial mount effect already trusts, asked again here.
   */
  const revalidateAfterReconnect = useCallback(
    async (expectedSessionId: string) => {
      const jwtNow = jwtRef.current;
      if (!jwtNow) return;

      try {
        const res = await fetch(
          `${baseRef.current}/second-screen/active?gameId=${encodeURIComponent(gameId)}`,
          { headers: { Authorization: `Bearer ${jwtNow}` } },
        );
        if (!mountedRef.current || sessionIdRef.current !== expectedSessionId) return;

        if (res.ok) {
          const body = (await res.json()) as { sessionId: string };
          if (body.sessionId === expectedSessionId) return;
        }

        // Either nothing is active any more, or a different session replaced
        // this one: either way this desktop is not really paired, and a fan
        // deserves the same explanation the live `revoked` event gives,
        // not silence just because this end found out about it late.
        endSession(PHONE_GONE_MESSAGE);
      } catch {
        // A failed check here is not itself proof the pairing ended; the
        // next reconnect (or the next real event on the stream) gets
        // another chance to notice.
      }
    },
    [endSession, gameId],
  );

  const openStream = useCallback(
    (sessionId: string) => {
      closeStream();
      const jwtNow = jwtRef.current;
      if (!jwtNow) return;

      // Local to this stream instance: true only for its very first
      // connection, so the initial connect (right after requestPairing, or
      // right after the mount effect's own active check) never re-triggers
      // the active check above. Every connection after that is a genuine
      // reconnect and gets revalidated.
      let firstConnection = true;

      streamDisposeRef.current = openSecondScreenStream({
        url: `${baseRef.current}/second-screen/${sessionId}/stream`,
        getJwt: () => jwtRef.current ?? "",
        // An expired tenant token is an expiry, not the end of the pairing.
        refreshAuth: () => refreshAuthRef.current?.() ?? Promise.resolve(null),
        onConnectedChange: (isConnected) => {
          if (mountedRef.current) setConnected(isConnected);
          if (isConnected) {
            if (firstConnection) {
              firstConnection = false;
            } else {
              void revalidateAfterReconnect(sessionId);
            }
          }
        },
        onEvent: ({ event, data }) => {
          if (!mountedRef.current) return;

          if (event === "paired") {
            clearExpiryTimer();
            setStatus("paired");
            setCode(null);
            setError(null);
            return;
          }

          /*
           * The phone has asked this screen to do something. Reported, not
           * run: this hook owns the stream and nothing else, and the executor
           * has to claim the command server-side before acting on it, because
           * every tab on this game is receiving this same event.
           */
          if (event === "command") {
            const commandId = data?.commandId;
            if (typeof commandId === "string") onCommandRef.current?.(commandId);
            return;
          }

          if (event === "revoked") {
            // Only the phone-went-away case gets a message: the fan should
            // know why their panel came back on its own. Any other reason
            // (they hit Disconnect themselves, on either end) needs no
            // explanation.
            endSession(data?.reason === "phone_gone" ? PHONE_GONE_MESSAGE : null);
          }
        },
        // The token this desktop redeemed with expired, or the session was
        // revoked so cleanly the server never got to publish a `revoked`
        // event for this stream to hear. Same ending either way.
        onTerminal: () => {
          if (mountedRef.current) {
            endSession("This screen's pairing session ended. Reload the page to pair a phone again.");
          }
        },
      });
    },
    [clearExpiryTimer, closeStream, endSession, revalidateAfterReconnect],
  );

  const authHeaders = useCallback(
    (extra?: Record<string, string>) => ({
      Authorization: `Bearer ${jwtRef.current}`,
      ...extra,
    }),
    [],
  );

  const requestPairing = useCallback(async (): Promise<void> => {
    if (!jwtRef.current || !enabledRef.current) return;

    try {
      const res = await fetch(`${baseRef.current}/second-screen`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ gameId, baseUrl: window.location.origin }),
      });
      if (!res.ok) {
        if (mountedRef.current) setError("Could not start pairing. Try again.");
        return;
      }

      const body = (await res.json()) as SecondScreenCode;
      if (!mountedRef.current) return;

      setSessionId(body.sessionId);
      setCode(body);
      setStatus("pairing");
      setError(null);
      openStream(body.sessionId);

      // The code expires. Rather than leave a dead QR on screen, ask for a
      // fresh one right as this one lapses, but only if the fan is still on
      // the pairing step: a phone that claimed it, or a fan who navigated
      // away, means nobody is watching this code any more.
      clearExpiryTimer();
      const msUntilExpiry = new Date(body.expiresAt).getTime() - Date.now();
      expiryTimerRef.current = setTimeout(() => {
        if (mountedRef.current) void requestPairing();
      }, Math.max(0, msUntilExpiry));
    } catch {
      if (mountedRef.current) setError("Could not start pairing. Try again.");
    }
  }, [authHeaders, clearExpiryTimer, gameId, openStream]);

  const cancelPairing = useCallback((): void => {
    const sessionId = sessionIdRef.current;
    clearExpiryTimer();
    closeStream();
    setSessionId(null);
    setStatus("idle");
    setCode(null);
    setError(null);
    setConnected(false);

    // Best effort and not awaited: this frees the PIN immediately instead of
    // leaving it live until it naturally expires, but nothing here depends
    // on the request landing.
    if (sessionId && jwtRef.current) {
      fetch(`${baseRef.current}/second-screen/${sessionId}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).catch(() => {});
    }
  }, [authHeaders, clearExpiryTimer, closeStream]);

  const disconnect = useCallback(async (): Promise<void> => {
    const sessionId = sessionIdRef.current;
    clearExpiryTimer();
    closeStream();
    setSessionId(null);

    // Moves to idle immediately, without waiting for the DELETE or the
    // revoked event it triggers, so the button feels instant rather than
    // pending on a round trip.
    setStatus("idle");
    setCode(null);
    setError(null);
    setConnected(false);

    if (!sessionId || !jwtRef.current) return;
    try {
      await fetch(`${baseRef.current}/second-screen/${sessionId}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
    } catch {
      // The UI has already moved on; nothing more to reconcile here.
    }
  }, [authHeaders, clearExpiryTimer, closeStream]);

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    // Signed out, or this tenant does not even have the layer switched on:
    // nobody to pair on behalf of, and no session to leak, so this makes no
    // requests at all. Without the `enabled` half of this, a tenant with the
    // layer off still fired `GET /second-screen/active` on every Watch page
    // load, which lands on the SPA's own origin when
    // VITE_AUDIENCE_LAYER_API_URL is unset and tries to parse HTML as JSON.
    if (!jwt || !enabled) {
      setStatus("idle");
      setCode(null);
      setError(null);
      return () => {
        mountedRef.current = false;
      };
    }

    void (async () => {
      try {
        const res = await fetch(
          `${baseRef.current}/second-screen/active?gameId=${encodeURIComponent(gameId)}`,
          { headers: { Authorization: `Bearer ${jwt}` } },
        );
        if (cancelled || !mountedRef.current) return;

        // A phone already owns this game: this desktop, or a second tab of
        // it, blocks too, and starts watching the same stream that will
        // carry the eventual revoked event.
        if (res.ok) {
          const body = (await res.json()) as { sessionId: string };
          if (cancelled || !mountedRef.current) return;
          setSessionId(body.sessionId);
          setStatus("paired");
          setError(null);
          openStream(body.sessionId);
          return;
        }

        if (res.status === 404) {
          setStatus("idle");
          return;
        }

        setStatus("idle");
        setError("Could not check this game's pairing status.");
      } catch {
        if (!cancelled && mountedRef.current) {
          setStatus("idle");
          setError("Could not check this game's pairing status.");
        }
      }
    })();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      clearExpiryTimer();
      closeStream();
      setSessionId(null);
      // A gameId change tears down the previous game's session state right
      // here, rather than leaving it on screen until the new game's own
      // active check resolves. Without this a fan switching games could see
      // the old game's QR code or paired card flash for a moment on the new
      // one.
      setStatus("idle");
      setCode(null);
      setError(null);
      setConnected(false);
    };
    // openStream, clearExpiryTimer and closeStream are stable across
    // renders: all of them close over refs rather than gameId/jwt directly,
    // including openStream's own chain through revalidateAfterReconnect
    // (which does depend on gameId, but only to read it, never to trigger a
    // re-run on its own since gameId is already listed below). So gameId,
    // jwt and enabled are the only real triggers here: a new game, signing
    // in or out, or the tenant's own layer flag resolving after this hook's
    // first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, jwt, enabled]);

  return { status, code, error, connected, sessionId, requestPairing, cancelPairing, disconnect };
}
